import { type ReactElement, useContext, useEffect, useRef } from 'react';

import type {
  MenuItemProps as RACMenuItemProps,
  SelectionMode,
} from 'react-aria-components/Menu';
import {
  MenuStateContext,
  MenuItem as RACMenuItem,
} from 'react-aria-components/Menu';

import type { IconName } from '../icon/generated_icons';
import { Icon } from '../icon/icon';
import { Text } from '../text/text';

import styles from './menu.module.css';

export type MenuItemProps = RACMenuItemProps & {
  label?: string | ReactElement;
  icon?: IconName;
  beforeElement?: ReactElement;
  submenuChildren?: MenuItemProps[];
  element?: ReactElement;
  afterElement?: ReactElement;
  isSeparator?: boolean;
  omitContainerStylesForCustomElement?: boolean;
  selectionMode?: SelectionMode;
  headerTitle?: string;
  helpMessage?: string;
};

/**
 * react-aria keeps the last hovered item's [data-focused] highlight when the
 * pointer moves onto a disabled item, a separator, a header or menu
 * whitespace, because those don't participate in hover handling. This marker
 * clears the focused key whenever the pointer leaves an item, matching native
 * menu behavior. It skips clearing when the pointer moves onto another
 * enabled item (react-aria moves the highlight there itself, before
 * pointerleave fires) and when the item's submenu is open. It must be
 * rendered inside the item's render prop: MenuItem itself renders in
 * react-aria's hidden collection tree where MenuStateContext is not
 * available.
 */
function ClearFocusedKeyOnLeave() {
  const menuState = useContext(MenuStateContext);
  const markerRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const itemElement =
      markerRef.current?.closest<HTMLElement>('[role^="menuitem"]');
    if (!itemElement || !menuState) {
      return;
    }
    const menuElement = itemElement.closest('[role="menu"]');
    const onPointerLeave = (event: PointerEvent) => {
      if (itemElement.getAttribute('aria-expanded') === 'true') {
        return;
      }
      const relatedItem =
        event.relatedTarget instanceof Element
          ? event.relatedTarget.closest(
              '[role^="menuitem"]:not([data-disabled])',
            )
          : null;
      const movedToEnabledItemInSameMenu =
        relatedItem !== null &&
        relatedItem.closest('[role="menu"]') === menuElement;
      if (movedToEnabledItemInSameMenu) {
        return;
      }
      menuState.selectionManager.setFocusedKey(null);
    };
    itemElement.addEventListener('pointerleave', onPointerLeave);
    return () => {
      itemElement.removeEventListener('pointerleave', onPointerLeave);
    };
  }, [menuState]);

  return <span ref={markerRef} hidden />;
}

export function MenuItem(props: MenuItemProps) {
  if (props.element) {
    return (
      <RACMenuItem
        className={
          props.omitContainerStylesForCustomElement
            ? styles.custom_item_container
            : styles.item_container
        }
        {...props}
      >
        {({ selectionMode: parentSelectionMode, isSelected }) => {
          const selectionMode = props.selectionMode ?? parentSelectionMode;
          const checkmarkElement = isSelected ? (
            <div className={styles.item_left}>
              <Icon className={styles.check_icon} name="checkmark" />
            </div>
          ) : (
            <div className={styles.item_left} />
          );
          return (
            <>
              <ClearFocusedKeyOnLeave />
              {selectionMode !== 'none' ? checkmarkElement : null}
              {props.element}
            </>
          );
        }}
      </RACMenuItem>
    );
  }

  return (
    <RACMenuItem
      onAction={() => {
        props?.onAction?.();
      }}
      className={styles.item_container}
      {...props}
    >
      {({ hasSubmenu, selectionMode: parentSelectionMode, isSelected }) => {
        const selectionMode = props.selectionMode ?? parentSelectionMode;
        const checkmarkElement = isSelected ? (
          <div className={styles.item_left}>
            <Icon className={styles.check_icon} name="checkmark" />
          </div>
        ) : (
          <div className={styles.item_left} />
        );

        return (
          <>
            <ClearFocusedKeyOnLeave />
            {!hasSubmenu && selectionMode !== 'none' ? checkmarkElement : null}

            <div className={styles.item_label_container}>
              {props.beforeElement ? props.beforeElement : null}
              {props.icon ? <Icon name={props.icon} /> : null}
              <span className={styles.item_label}>
                <Text
                  elementType="span"
                  color="inherit"
                  variant="value"
                  slot="label"
                >
                  {props.label}
                </Text>
                {props.helpMessage ? (
                  <Text slot="description" variant="label">
                    {props.helpMessage}
                  </Text>
                ) : null}
              </span>
            </div>

            {props.afterElement ? (
              <div className={styles.item_more}>{props.afterElement}</div>
            ) : null}

            {hasSubmenu ? <Icon name="chevron_small_isolated_e" /> : null}
          </>
        );
      }}
    </RACMenuItem>
  );
}
